// ============================================================================
// useAllRoleHistory.js — read-only fetch of the entire user_role_history table
// ============================================================================
// useUserRoleHistory.js is scoped to a single userId (the role-history editor).
// The analytics dashboard needs every employee's full role history at once to
// render role-segmented tenure bars, so this hook fetches all rows in one go
// and groups them by user.
//
// Conventions:
//   DB: snake_case  |  UI: camelCase
//   Read-only — no RLS write guard needed.
// ============================================================================

import { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';

export function useAllRoleHistory() {
  const [roleHistoryByUser, setRoleHistoryByUser] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const { data, error } = await supabase
          .from('user_role_history')
          .select('user_id, job_role, job_level, office_location, effective_from, effective_to')
          .order('effective_from', { ascending: true });

        if (error) throw error;

        const byUser = {};
        (data || []).forEach((r) => {
          const uid = String(r.user_id);
          if (!byUser[uid]) byUser[uid] = [];
          byUser[uid].push({
            jobRole: r.job_role || '',
            jobLevel: r.job_level ?? null,
            officeLocation: r.office_location || null,
            effectiveFrom: r.effective_from,
            effectiveTo: r.effective_to || null,
          });
        });

        if (!cancelled) setRoleHistoryByUser(byUser);
      } catch (err) {
        console.error('[useAllRoleHistory] fetch error:', err);
        if (!cancelled) setRoleHistoryByUser({});
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return { roleHistoryByUser, loading };
}

export default useAllRoleHistory;
